import type { Story } from '@/types/cmsTypes';
import { v1 } from '@/infrastructure/api/v1/client';
import { listAll } from '@/infrastructure/api/v1/lists';
import { sceneToLegacy } from '@/infrastructure/api/v1/mappers/scenes';
import { makeVersionOption } from '@/components/publish-flow/versionOptions';
import type { AnchorCategory, ProjectSceneTarget, PublishVersionStatus } from '@/components/publish-flow/types';
import { splitDescription } from '@/modules/library/utils/storyAttributionUtils';
import projectPlaceholder from '@/assets/project-historical.jpg';
import { toStoryRow } from './loadProjectPage';

export interface ProjectSceneTargets {
  targets: ProjectSceneTarget[];
  introById: Record<string, string>;
}

// One stories read and one versions read for the whole project, covers from the page's cards. Throws on a failed read
export async function buildProjectSceneTargets(projectId: string, pageStories: Story[]): Promise<ProjectSceneTargets> {
  const [storiesResult, scenesResult] = await Promise.all([
    listAll(page => v1.GET('/api/v1/stories', { params: { query: { projectUniqueId: [projectId], ...page } } })),
    listAll(page => v1.GET('/api/v1/scenes', { params: { query: { projectUniqueId: [projectId], ...page } } })),
  ]);
  if (storiesResult.error) throw storiesResult.error;
  if (scenesResult.error) throw scenesResult.error;

  const origins = storiesResult.data.map(toStoryRow);
  const visibilityById = new Map(storiesResult.data.map(dto => [dto.uniqueId, dto.visibility]));
  const coverById = new Map(
    pageStories.filter(s => s.image && s.image !== projectPlaceholder).map(s => [s.id, s.image]),
  );

  const grouped: Record<string, ProjectSceneTarget> = {};
  for (const inst of scenesResult.data.map(sceneToLegacy)) {
    const oid = inst.scene_origin_id as string;
    const origin = origins.find(o => o.scene_origin_id === oid);
    if (!origin) continue;
    grouped[oid] ??= {
      sceneOriginId: oid,
      sceneOriginName: origin.scene_origin_name || 'Untitled story',
      orderNumber: origin.scene_origin_order_number ?? 0,
      coverImageUrl: coverById.get(oid),
      visibility: visibilityById.get(oid) ?? undefined,
      versions: [],
    };
    grouped[oid].versions.push({
      ...makeVersionOption({
        sceneInstanceId: inst.scene_instance_id,
        label: inst.scene_instance_display_name || `v${inst.scene_instance_version_number ?? 1}`,
        status: (inst.scene_instance_status as PublishVersionStatus) || 'draft',
        versionNumber: inst.scene_instance_version_number ?? undefined,
      }),
      anchorCategory: (inst.scene_instance_anchor_category as AnchorCategory | null) ?? undefined,
    });
  }

  const targets = Object.values(grouped)
    .map(t => ({ ...t, versions: t.versions.sort((a, b) => (b.versionNumber ?? 0) - (a.versionNumber ?? 0)) }))
    .sort((a, b) => a.orderNumber - b.orderNumber);

  const introById: Record<string, string> = {};
  for (const o of origins) {
    introById[o.scene_origin_id as string] = splitDescription((o.scene_origin_description as string) || '').userText;
  }
  return { targets, introById };
}
